import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { get_api as getApi, post_api as postApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import CustomModal from '../../util_components/custom_modal';
import PeriodFilter from './PeriodFilter';
import StatusMultiSelect from './StatusMultiSelect';
import { readPeriodParams, resolvePeriod } from './period_utils';
import { downloadCsv } from './csv_utils';
import './trial_points.css';

const CONVERSION_LABELS = { conversion_1on1: '1on1', conversion_gc: 'GC' };
const ADJUSTMENT_LABELS = {
  initial: 'Initial grant',
  cms_add: 'Added by care team',
  cms_remove: 'Removed by care team'
};

// /trial_points/:teacher_uuid — one teacher's period report; period comes from the URL params.
function TrialPointsTeacherDetail() {
  const history = useHistory();
  const location = useLocation();
  const { teacher_uuid: teacherUuid } = useParams();
  const ianaTimezone = useSelector((state) => state.home.iana_timezone);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [pointsModal, setPointsModal] = useState(null); // null | 'add' | 'remove'
  const [pointsValue, setPointsValue] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { range, customStart, customEnd } = readPeriodParams(location.search);
  const { startDate, endDate } = resolvePeriod(range, customStart, customEnd);
  const statusesKey = new URLSearchParams(location.search).get('statuses') || '';
  const statuses = statusesKey.split(',').filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const qp = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (statusesKey) qp.append('statuses', statusesKey);
    if (ianaTimezone) qp.append('timezone', ianaTimezone);
    const url = getApiUrl(
      `/v2/cms_user/trial_points/${teacherUuid}/summary?${qp.toString()}`,
      '',
      true
    );
    getApi(url, true)
      .then((res) => {
        if (!cancelled) setReport(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load the teacher report. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teacherUuid, startDate, endDate, refreshKey, statusesKey, ianaTimezone]);

  const onStatusesChange = (next) => {
    const p = new URLSearchParams(location.search);
    if (next.length) p.set('statuses', next.join(','));
    else p.delete('statuses');
    history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
  };

  const onDownloadCsv = () => {
    const rows = (report?.sessions || []).map((row) => [
      row.student_name || row.student_uuid || '',
      row.session_start
        ? moment(row.session_start).tz(ianaTimezone).format('YYYY-MM-DD HH:mm z')
        : '',
      row.status_text || row.status || '',
      row.duration_minutes || '',
      row.lead_score ?? '',
      row.points_consumed || 0,
      row.multiplier ? `${row.multiplier}x` : '',
      row.outcome_text || '',
      row.exception_reason || '',
      row.points_refunded || 0,
      row.conversion_points || 0,
      row.session_uuid
    ]);
    downloadCsv(
      `trial-points-${teacherUuid}-${startDate}-to-${endDate}.csv`,
      [
        'Student',
        'Session Date',
        'Status',
        'Duration (mins)',
        'Lead Score',
        'Deducted',
        'Multiplier',
        'Outcome',
        'Exception Reason',
        'Refunded',
        'Conversion',
        'Session UUID'
      ],
      rows
    );
  };

  const openPointsModal = (action) => {
    setPointsValue('');
    setPointsReason('');
    setModalError('');
    setPointsModal(action);
  };

  const closePointsModal = () => {
    if (!submitting) setPointsModal(null);
  };

  const submitPoints = (e) => {
    e.preventDefault();
    const points = parseInt(pointsValue, 10);
    const reason = pointsReason.trim();
    if (Number.isNaN(points) || points <= 0) {
      setModalError('Points must be a number greater than zero');
      return;
    }
    if (!reason) {
      setModalError('Reason is required');
      return;
    }
    setModalError('');
    setSubmitting(true);
    const url = getApiUrl(
      `/v2/cms_user/teachers/${teacherUuid}/update_teacher_session_points`,
      '',
      true
    );
    postApi(url, { action: pointsModal, number_of_points: points, reason }, true)
      .then(() => {
        setPointsModal(null);
        setRefreshKey((k) => k + 1); // re-fetch the report so tiles + tables show the change
      })
      .catch((err) => {
        setModalError(err?.response?.data?.reason || 'Failed to update points. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const summary = report?.summary || {};
  const sessions = report?.sessions || [];
  const adjustments = report?.adjustments || [];
  const teacherName = report?.teacher_name || teacherUuid;

  const pointsTiles = [
    { label: 'Starting', value: summary.starting_balance, color: 'var(--text-primary)' },
    { label: 'Added', value: summary.points_added, color: 'var(--state-success)' },
    { label: 'Deducted', value: summary.points_deducted, color: 'var(--state-error)' },
    {
      label: 'Remaining',
      value: summary.ending_balance,
      color: 'var(--brand-primary)',
      highlight: true
    }
  ];
  const conversionTiles = [
    { label: '1on1 conversions', value: summary.conversions_1on1, color: 'var(--text-primary)' },
    { label: 'GC conversions', value: summary.conversions_gc, color: 'var(--text-primary)' }
  ];

  const renderTile = (tile) => (
    <div
      key={tile.label}
      style={{
        flex: '1 1 150px',
        background: 'var(--surface-card)',
        border: `1px solid ${tile.highlight ? 'var(--brand-primary)' : 'var(--border-default)'}`,
        borderRadius: '8px',
        padding: '16px 20px'
      }}
    >
      <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {tile.label}
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: tile.color, marginTop: '4px' }}>
        {tile.value ?? 0}
      </div>
    </div>
  );

  const renderConversion = (row) => {
    if (!row.conversion_points) return '—';
    const label = CONVERSION_LABELS[row.conversion_type] || '';
    return `+${row.conversion_points}${label ? ` (${label})` : ''}`;
  };

  const renderSessionRow = (row) => (
    <div className="tp-row" key={row.session_uuid}>
      <div className="tp-col tp-left tp-wide" style={{ fontWeight: 600 }}>
        {row.student_uuid ? (
          <a
            href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.student_name || row.student_uuid}
          </a>
        ) : (
          '—'
        )}
      </div>
      <div className="tp-col tp-left tp-wide">
        {row.session_start
          ? moment(row.session_start).tz(ianaTimezone).format('MMM DD, YYYY · hh:mm A z')
          : '—'}
      </div>
      <div className="tp-col">
        <div>{row.status_text || row.status || '—'}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
          {row.duration_minutes ? `(${row.duration_minutes} mins)` : ''}
        </div>
      </div>
      <div className="tp-col">{row.lead_score ?? '—'}</div>
      <div className="tp-col" style={{ color: 'var(--state-error)', fontWeight: 600 }}>
        <div>
          {row.points_consumed ? (
            <>
              {row.points_consumed}
              {row.multiplier ? ` (${row.multiplier}x)` : ''}
            </>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>0</span>
          )}
        </div>
        {/* PRD 9.2: when the deduction was zeroed or overridden by an ops exception, the reason
            belongs next to the number — otherwise care has to open the session to find out why. */}
        {row.exception_reason ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontWeight: 400 }}>
            ({row.exception_reason})
          </div>
        ) : null}
        {/* PRD Section 6: which matrix row produced this charge. Absent on pre-Section-6 rows. */}
        {row.outcome_text ? (
          <div
            style={{ color: 'var(--text-muted)', fontSize: '13px', fontWeight: 400 }}
            title={row.deduction_outcome}
          >
            {row.outcome_text}
          </div>
        ) : null}
      </div>
      <div className="tp-col" style={{ color: 'var(--state-success)', fontWeight: 600 }}>
        {row.points_refunded ? (
          `+${row.points_refunded}`
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </div>
      <div className="tp-col" style={{ color: 'var(--state-success)', fontWeight: 600 }}>
        {row.conversion_points ? (
          renderConversion(row)
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </div>
      <div className="tp-col">
        <a
          href={`/sessiondetails?id=${encodeURIComponent(row.session_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="link-btn"
        >
          Details
        </a>
      </div>
    </div>
  );

  const renderAdjustmentRow = (row) => (
    <div className="tp-row" key={row.id}>
      <div className="tp-col tp-left tp-wide">
        {moment(row.date).tz(ianaTimezone).format('MMM DD, YYYY · hh:mm A z')}
      </div>
      <div className="tp-col tp-left">{ADJUSTMENT_LABELS[row.type] || row.type}</div>
      <div className="tp-col">{row.points > 0 ? `+${row.points}` : row.points}</div>
      <div className="tp-col tp-left">{row.cms_user_name || '—'}</div>
      <div className="tp-col tp-left tp-wide">{row.reason || '—'}</div>
    </div>
  );

  return (
    <div className="common-page-container">
      <div className="tp-page-head">
        <div className="tp-title">Trial Points — {teacherName}</div>
        <div className="tp-actions-stack">
          <div className="tp-actions">
            <a
              href={`/teacherprofile?id=${encodeURIComponent(teacherUuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="link-btn"
            >
              Teacher profile
            </a>
            <a
              href={`/usersessions/future?id=${encodeURIComponent(teacherUuid)}&user_type=TEACHER&name=${encodeURIComponent(teacherName)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="link-btn"
            >
              Sessions list
            </a>
          </div>
          <div className="tp-actions">
            <button type="button" className="link-btn" onClick={() => openPointsModal('add')}>
              Add Points
            </button>
            <button type="button" className="link-btn" onClick={() => openPointsModal('remove')}>
              Deduct Points
            </button>
          </div>
        </div>
      </div>
      <PeriodFilter />
      <CustomModal show={!!pointsModal} close={closePointsModal}>
        <div style={{ padding: '8px 4px', minWidth: '320px' }}>
          <div className="tp-section-title" style={{ marginBottom: '14px' }}>
            {pointsModal === 'add' ? 'Add Points' : 'Deduct Points'} — {teacherName}
          </div>
          <form onSubmit={submitPoints} className="tp-modal-form">
            <label className="tp-modal-label" htmlFor="tp-points-input">
              Points
              <input
                id="tp-points-input"
                type="number"
                min="1"
                step="1"
                value={pointsValue}
                placeholder="e.g. 10"
                onChange={(e) => setPointsValue(e.target.value)}
                className="tp-modal-input"
              />
            </label>
            <label className="tp-modal-label" htmlFor="tp-reason-input">
              Reason
              <input
                id="tp-reason-input"
                type="text"
                value={pointsReason}
                placeholder="Why are the points changing?"
                onChange={(e) => setPointsReason(e.target.value)}
                className="tp-modal-input"
              />
            </label>
            {modalError ? (
              <div style={{ color: 'var(--state-error)', marginBottom: '10px' }}>{modalError}</div>
            ) : null}
            <div className="tp-actions">
              <button type="submit" className="link-btn" disabled={submitting}>
                {submitting ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </CustomModal>
      {error ? <div style={{ color: 'var(--state-error)' }}>{error}</div> : null}
      {loading ? <div style={{ color: 'var(--text-primary)' }}>Loading…</div> : null}
      {!loading && report ? (
        <>
          <div className="tp-tiles">
            {pointsTiles.map(renderTile)}
            <div className="tp-tiles-divider" />
            {conversionTiles.map(renderTile)}
          </div>
          <div className="tp-section-head">
            <span className="tp-section-title">Trial sessions</span>
            <span className="tp-section-count">{sessions.length}</span>
            <div className="tp-actions" style={{ marginLeft: 'auto' }}>
              <StatusMultiSelect selected={statuses} onChange={onStatusesChange} />
              <button
                type="button"
                className="link-btn"
                style={{ margin: 0 }}
                disabled={!sessions.length}
                onClick={onDownloadCsv}
              >
                Download CSV
              </button>
            </div>
          </div>
          <div className="tp-table">
            <div className="tp-row tp-head">
              <div className="tp-col tp-left tp-wide">Student</div>
              <div className="tp-col tp-left tp-wide">Session date</div>
              <div className="tp-col">Status (Duration)</div>
              <div className="tp-col">Lead score</div>
              <div className="tp-col">Deducted</div>
              <div className="tp-col">Refunded</div>
              <div className="tp-col">Conversion</div>
              <div className="tp-col">Details</div>
            </div>
            {sessions.length === 0 ? (
              <div className="tp-row">
                <div className="tp-col tp-left">No trial sessions in this period</div>
              </div>
            ) : (
              sessions.map(renderSessionRow)
            )}
          </div>
          {adjustments.length > 0 ? (
            <>
              <div className="tp-section-head">
                <span className="tp-section-title">Other adjustments</span>
                <span className="tp-section-count">{adjustments.length}</span>
              </div>
              <div className="tp-table">
                <div className="tp-row tp-head">
                  <div className="tp-col tp-left tp-wide">Date</div>
                  <div className="tp-col tp-left">Type</div>
                  <div className="tp-col">Points</div>
                  <div className="tp-col tp-left">By</div>
                  <div className="tp-col tp-left tp-wide">Reason</div>
                </div>
                {adjustments.map(renderAdjustmentRow)}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default TrialPointsTeacherDetail;
